import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { 
  GlobeAltIcon, 
  ChartBarIcon, 
  UserGroupIcon, 
  BuildingOffice2Icon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'

const HomePage = () => {
  const services = [
    {
      icon: GlobeAltIcon,
      title: "International Market Entry",
      description: "Strategic guidance for entering emerging markets with comprehensive risk assessment and opportunity analysis.",
      link: "/services/market-entry"
    },
    {
      icon: ChartBarIcon,
      title: "Business Development",
      description: "Growth strategies and partnership facilitation to expand your business presence in target markets.",
      link: "/services/business-development"
    },
    {
      icon: UserGroupIcon,
      title: "Strategic Consulting",
      description: "Expert advisory services for complex international business decisions and regulatory compliance.",
      link: "/services/consulting"
    },
    {
      icon: BuildingOffice2Icon,
      title: "Corporate Partnerships",
      description: "Facilitating strategic alliances and joint ventures between international business partners.",
      link: "/services/partnerships"
    }
  ]

  const testimonials = [
    {
      quote: "Nexus Global Solutions transformed our approach to international expansion. Their expertise in emerging markets is unparalleled.",
      author: "Sarah Chen",
      title: "CEO, TechVenture International",
      company: "TechVenture International"
    },
    {
      quote: "The strategic partnerships they facilitated have been instrumental in our 300% growth over the past two years.",
      author: "Dr. Michael Rodriguez",
      title: "Director of Global Operations",
      company: "MedTech Solutions"
    },
    {
      quote: "Their deep understanding of regulatory landscapes saved us months of compliance work in three different markets.",
      author: "Elena Volkov",
      title: "VP International Affairs",
      company: "Astoria Energy Group"
    }
  ]

  return (
    <>
      <Helmet>
        <title>Nexus Global Solutions - International Business Consulting</title>
        <meta name="description" content="Leading international business consulting firm specializing in emerging markets and strategic partnerships across the globe." />
        <meta name="keywords" content="international business consulting, emerging markets, strategic partnerships, market entry, business development" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-800 to-blue-900 text-white overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/images/locations/valdoria-hq-exterior.png')"
          }}
        ></div>
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Nexus Global Solutions
            </h1>
            <div className="text-xl md:text-2xl font-medium mb-6 text-yellow-200">
              Connecting Markets, Creating Opportunities
            </div>
            <p className="text-lg md:text-xl text-gray-100 mb-8 max-w-2xl leading-relaxed">
              Leading international business consulting firm specializing in emerging markets and strategic partnerships across the globe.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to="/about"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center justify-center transition-colors"
              >
                Explore Our Services
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/contact"
                className="border border-white text-white hover:bg-white hover:text-gray-900 px-6 py-3 rounded-lg font-medium inline-flex items-center justify-center transition-colors"
              >
                Get Consultation
              </Link>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-yellow-500 bg-opacity-20 rounded-lg">
                    <GlobeAltIcon className="h-8 w-8 text-yellow-200" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">45+</div>
                    <div className="text-gray-200 font-medium">Countries Served</div>
                    <div className="text-sm text-gray-300">Global Reach</div>
                  </div>
                </div>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-yellow-500 bg-opacity-20 rounded-lg">
                    <BuildingOffice2Icon className="h-8 w-8 text-yellow-200" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">200+</div>
                    <div className="text-gray-200 font-medium">Successful Projects</div>
                    <div className="text-sm text-gray-300">Proven Results</div>
                  </div>
                </div>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-yellow-500 bg-opacity-20 rounded-lg">
                    <UserGroupIcon className="h-8 w-8 text-yellow-200" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">175</div>
                    <div className="text-gray-200 font-medium">Expert Consultants</div>
                    <div className="text-sm text-gray-300">Professional Team</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Core Services
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Comprehensive solutions for international business expansion and strategic growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.title}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mb-6">
                    <service.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4" data-service={service.title.toLowerCase().replace(/\s+/g, '-')}>
                    {service.title}
                  </h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <Link
                    to={service.link}
                    className="text-blue-600 font-medium inline-flex items-center hover:text-blue-700 transition-colors duration-200"
                  >
                    Learn More
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Trusted by Global Leaders
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Since 2018, Nexus Global Solutions has been at the forefront of international 
                business consulting, helping companies navigate complex global markets and 
                establish meaningful partnerships.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div data-stat="client-retention">
                  <div className="text-3xl font-bold text-blue-600">98.5%</div>
                  <div className="text-gray-600">Client Retention</div>
                </div>
                <div data-stat="success-rate">
                  <div className="text-3xl font-bold text-blue-600">94%</div>
                  <div className="text-gray-600">Success Rate</div>
                </div>
              </div>
              <Link to="/about" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center transition-colors">
                Learn About Our Story
              </Link>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <img 
                  src="/images/locations/corporate-boardroom.png" 
                  alt="Nexus Global Solutions Boardroom"
                  className="w-full h-64 object-cover"
                />
                <div className="p-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Our Corporate Headquarters
                  </h3>
                  <p className="text-gray-600 mb-6">
                    State-of-the-art facilities in Valdoria City, equipped with advanced technology 
                    for seamless international collaboration and client meetings.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      <span className="text-sm text-gray-600">Advanced Video Conferencing</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      <span className="text-sm text-gray-600">24/7 Global Communication Hub</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      <span className="text-sm text-gray-600">Secure Client Meeting Facilities</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Success stories from companies that trusted us with their international expansion
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.author}
                className="bg-white rounded-lg shadow-lg overflow-hidden p-8" 
                data-testimonial={testimonial.author.toLowerCase().replace(/\s+/g, '-')}
              >
                <div className="mb-6">
                  <div className="text-4xl text-blue-600 mb-4">"</div>
                  <p className="text-gray-700 leading-relaxed italic">
                    {testimonial.quote}
                  </p>
                </div>
                <div className="border-t border-gray-200 pt-6">
                  <div className="font-semibold text-gray-900" data-author={testimonial.author}>
                    {testimonial.author}
                  </div>
                  <div className="text-gray-600" data-title={testimonial.title}>
                    {testimonial.title}
                  </div>
                  <div className="text-sm text-gray-500" data-company={testimonial.company}>
                    {testimonial.company}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Expand Globally?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Let's discuss how Nexus Global Solutions can help your business thrive in international markets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 rounded-lg font-medium inline-flex items-center justify-center transition-colors">
                Schedule Consultation
              </Link>
              <Link to="/about" className="border border-white text-white hover:bg-white hover:text-gray-900 px-6 py-3 rounded-lg font-medium inline-flex items-center justify-center transition-colors">
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Educational data for scraping practice */}
      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="homepage" data-content-type="landing">
          <span data-services-count="4">4 Core Services</span>
          <span data-testimonials-count="3">3 Client Testimonials</span>
          <span data-stats-client-retention="98.5">98.5% Client Retention</span>
          <span data-stats-success-rate="94">94% Success Rate</span>
          <span data-recent-investments="50000000">$50M+ Recent Investments</span>
          <span data-partnerships-2024="25">25 Partnerships in 2024</span>
        </div>
      </div>
    </>
  )
}

export default HomePage
